/**
 * The front door's proof: the whole library, drifting past.
 *
 * Two rows of real lesson frames scrolling in opposite directions, forever. The
 * counter-motion is the point — two rows travelling the same way read as one
 * sliding block, while opposed rows read as a library with depth, and the eye
 * keeps finding new frames instead of tracking one.
 *
 * The tiles are the real catalogue (public endpoint) — every one links to a
 * lesson a visitor can watch a minute after signing in — but the ART is purpose
 * -made concept work rather than the lessons' own thumbnails. Those thumbnails
 * are title cards with the topic baked into the pixels, which reads as a poster
 * wall and fights the caption drawn over it; concept art shows the IDEA and
 * leaves the words to the interface.
 *
 * The loop is CSS-only. The track holds the row twice and translates by exactly
 * -50%, so the second copy lands where the first began and the seam never
 * appears; there is no JS timer, no scroll listener, and nothing to drift out of
 * sync on a slow device.
 */

import { useEffect, useMemo, useState } from 'react';
import { cn } from '../../lib/utils';
import { fetchCatalog, type CatalogItem } from '../../services/learnAPI';
import { SubjectArt, subjectMeta, inkMuted } from './learnShared';
import { conceptArtFor } from './conceptArt';

/** Enough tiles that a row never looks sparse mid-scroll. */
const MIN_PER_ROW = 6;

function LessonTile({ item }: { item: CatalogItem }) {
  const meta = subjectMeta(item.subject);
  const art = conceptArtFor(item.shareToken) ?? item.thumbnailUrl;
  return (
    <figure className="group relative h-[132px] w-[212px] shrink-0 overflow-hidden rounded-[18px] bg-[#101018] shadow-[0_14px_34px_-20px_rgba(25,24,49,0.65)] ring-1 ring-white/50 dark:ring-white/[0.08] sm:h-[150px] sm:w-[242px]">
      {art ? (
        <img
          src={art}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
        />
      ) : (
        <SubjectArt subject={item.subject} />
      )}

      {/* Captions ride on ink, not on the frame: these range from deep space to
          a lit lab, and a caption keyed to one is unreadable on the other. */}
      <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-3 pb-2.5 pt-8">
        <span className="block text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: meta.bright }}>
          {item.subject}
          {item.grade ? ` · ${item.grade}` : ''}
        </span>
        <span className="mt-0.5 line-clamp-1 block text-[12.5px] font-semibold text-white">
          {item.title ?? 'Interactive lesson'}
        </span>
      </figcaption>
    </figure>
  );
}

function MarqueeRow({
  items,
  direction,
  seconds,
}: {
  items: CatalogItem[];
  direction: 'left' | 'right';
  seconds: number;
}) {
  // The row is rendered twice; -50% puts copy two exactly where copy one began.
  const doubled = [...items, ...items];
  return (
    <div
      className={cn(
        'flex w-max gap-3.5 will-change-transform',
        direction === 'left' ? 'animate-learn-marquee-left' : 'animate-learn-marquee-right',
      )}
      style={{ animationDuration: `${seconds}s` }}
    >
      {doubled.map((item, i) => (
        <LessonTile key={`${item.shareToken}-${i}`} item={item} />
      ))}
    </div>
  );
}

export function LessonShowcase({ className, style }: { className?: string; style?: React.CSSProperties }) {
  const [items, setItems] = useState<CatalogItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchCatalog()
      .then((rows) => {
        if (cancelled) return;
        setItems(rows.filter((r) => conceptArtFor(r.shareToken)));
      })
      .catch(() => { /* the fallback below carries the panel */ });
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => {
    if (items.length === 0) return null;
    // Interleave rather than slicing in half, so neither row is all one subject.
    const top = items.filter((_, i) => i % 2 === 0);
    const bottom = items.filter((_, i) => i % 2 === 1);
    const pad = (row: CatalogItem[]) => {
      const out = [...row];
      while (out.length && out.length < MIN_PER_ROW) out.push(...row);
      return out;
    };
    return { top: pad(top), bottom: pad(bottom) };
  }, [items]);

  // A front door must never depend on a network call to look finished.
  if (!rows) {
    return (
      <div className={cn('relative overflow-hidden rounded-[20px]', className)} style={style}>
        <div className="aspect-[16/7] w-full overflow-hidden rounded-[20px] opacity-70">
          <SubjectArt subject="Science" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative select-none', className)} style={style}>
      {/* The rows bleed off the left edge and dissolve into the page on the
          right, so the library reads as continuing past the screen. */}
      <div className="learn-marquee-mask group/marquee flex flex-col gap-3.5 overflow-hidden">
        <MarqueeRow items={rows.top} direction="left" seconds={64} />
        <MarqueeRow items={rows.bottom} direction="right" seconds={78} />
      </div>

      <p className={cn('mt-4 text-[12px]', inkMuted)}>
        Every tile is a chapter in the library.
      </p>
    </div>
  );
}
